/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line no-shadow
export const enum KEYS {
    ENTER = 13,
    ESCAPE = 27,
    ARROW_LEFT = 37,
    ARROW_UP = 38,
    ARROW_RIGHT = 39,
    ARROW_DOWN = 40,
    END = 35,
    HOME = 36,
}

const useTabControls = (
    items: Array<object>,
    initialIndex: number,
    callback?: (arg0: number) => void,
): [number, Dispatch<SetStateAction<number>>, RefObject<HTMLButtonElement[]>, () => void, () => void] => {
    const [activeItem, setActiveItem] = useState(initialIndex);
    const refsArray = useRef<HTMLButtonElement[]>([]);

    const goTo = useCallback(
        (direction: -1 | 1) => {
            const newIndex = (activeItem + direction + items.length) % items.length;
            setActiveItem(newIndex);
            refsArray.current[newIndex] && refsArray.current[newIndex].focus();
        },
        [activeItem, items.length],
    );

    const goToNext = useCallback(() => {
        const isRTL = document.documentElement.dir === 'rtl';
        isRTL ? goTo(-1) : goTo(1);
    }, [goTo]);

    const goToPrev = useCallback(() => {
        const isRTL = document.documentElement.dir === 'rtl';
        isRTL ? goTo(1) : goTo(-1);
    }, [goTo]);

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            // if the event does not originate from a tab owned by this component or from the body, ignore it
            // this is to prevent tab nav from triggering when using arrow keys in other situations
            if (![document.body, ...refsArray.current].some((el) => el === document.activeElement)) {
                return;
            }
            if (items.length > 1) {
                switch (event.which) {
                    case KEYS.ARROW_RIGHT:
                        event.preventDefault();
                        goToNext();
                        return;
                    case KEYS.ARROW_LEFT:
                        event.preventDefault();
                        goToPrev();
                        return;
                    case KEYS.HOME:
                        event.preventDefault();
                        setActiveItem(0);
                        return;
                    case KEYS.END:
                        event.preventDefault();
                        setActiveItem(items.length - 1);
                        return;
                    default:
                }
            }
        },
        [goToNext, goToPrev, items.length],
    );

    useEffect(() => {
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [onKeyDown]);

    useEffect(() => {
        callback && callback(activeItem);
    }, [activeItem, callback]);

    return [activeItem, setActiveItem, refsArray, goToNext, goToPrev];
};

export default useTabControls;
